import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useDebounce } from "../../../../hooks/useDebounce";
import { wishList } from "../../../../services/wishlistService";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { lngDetect } from "../../../../helpers/lngDetect";
import EventItemLoader from "../../../loader/EventItemLoader";
import EventItem from "../../../items/EventItem";
import Button from "../../../form/Button";
import NoData from "../../../elements/Nodata";
import noDataImage from "../../../../assets/images/dance.svg";
import { classNames } from "../../../../helpers/classNames";
import { t } from "i18next";

const EventsList = ({ listClass }) => {
  const { lang } = useSelector((state) => state?.langProvider);
  const [removedItems, setRemovedItems] = useState([]);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const queryClient = useQueryClient();
  const { data: usrData } = useSelector((state) => state?.profile);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 8,
    page: 1,
    sortQuery: "ordering",
  });

  const {
    data: wishlistData,
    isLoading: wishlistLoading,
    isFetching: wishlistFetching,
    refetch,
  } = useQuery({
    queryKey: ["wishlist", filters, search],
    queryFn: async (data) => {
      const res = await wishList({
        ...filters,
        ...(search && { keyword: search }),
        type: "wishlist",
        user: usrData?._id,
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      if (res?.docs?.status === 200) {
        const new_data_arr = res?.docs?.data?.map((item) => {
          const date = `${
            item?.eventData?.startDate
              ? dayjs(item?.eventData?.startDate).format(
                  item?.eventData?.endDate ? "DD" : "DD MMM YYYY"
                )
              : ""
          }${
            item?.eventData?.endDate
              ? " - " + dayjs(item?.eventData?.endDate).format("DD MMM YYYY")
              : ""
          }`;
          return {
            ...item,
            eventData: {
              ...item?.eventData,
              isWishlist: item?.isWishlist,
              isFollowing: item?.isFollowing,
              id: item?.eventData?._id,
              eventTitle: lngDetect(item?.eventData?.title),
              eventDate: date || "",
              eventLocation: item?.eventData?.address?.name,
              eventMinRate: item?.eventData?.minTicketPrice || false,
              images: item?.eventData?.gallery?.map((img) => img?.url) || [],
              tags:
                item?.eventData?.tagData?.map((item) =>
                  lngDetect(item?.eventData?.name)
                ) || [],
              link: `/event-details/${item?.eventData?.uid}`,
            },
          };
        });
        if (search) {
          return {
            ...res?.docs,
            data: [...new_data_arr],
          };
        } else {
          if (filters?.page > 1) {
            return {
              ...res?.docs,
              data: [...(wishlistData?.data || []), ...new_data_arr]?.filter(
                (item, index, self) =>
                  index === self.findIndex((t) => t?._id === item?._id)
              ),
            };
          } else {
            return {
              ...res?.docs,
              data: new_data_arr,
            };
          }
        }
      } else {
        return {
          ...res,
          data: [],
        };
      }
    },
    placeholderData: keepPreviousData,
  });

  async function addToFavoriteResponse(res) {
    if (res?.status === 200) {
      wishList({
        ...filters,
        ...(search && { keyword: search }),
        type: "wishlist",
        user: usrData?._id,
      }).then((resp) => {
        queryClient.setQueryData(["wishlist", filters, search], (state) => {
          const prevData = state?.data || [];

          // Remove the target item directly
          const filteredData = prevData.filter(
            (item) => item?.eventData?._id !== res?.data?._id
          );

          // Build a Set of existing IDs
          const existingIds = new Set(filteredData.map((item) => item?._id));

          // Only take new items that aren't already in state
          const result = (resp?.docs?.data || [])
            .filter((item) => !existingIds.has(item?._id))
            .map((item) => {
              const { eventData } = item || {};
              const start = eventData?.startDate
                ? dayjs(eventData.startDate).format(
                    eventData?.endDate ? "DD" : "DD MMM YYYY"
                  )
                : "";
              const end = eventData?.endDate
                ? " - " + dayjs(eventData.endDate).format("DD MMM YYYY")
                : "";

              return {
                ...item,
                eventData: {
                  ...eventData,
                  isWishlist: item?.isWishlist,
                  isFollowing: item?.isFollowing,
                  id: eventData?._id,
                  eventTitle: lngDetect(eventData?.title),
                  eventDate: start + end,
                  eventLocation: eventData?.address?.name,
                  eventMinRate: eventData?.minTicketPrice || false,
                  images: eventData?.gallery?.map((img) => img?.url) || [],
                  tags:
                    eventData?.tagData?.map((t) =>
                      lngDetect(t?.eventData?.name)
                    ) || [],
                  link: `/event-details/${eventData?.uid}`,
                },
              };
            });

          return {
            ...state,
            data: [...filteredData, ...result],
            metadata: resp?.docs?.metadata,
          };
        });
      });
    }
  }

  const loadMore = () => {
    if (wishlistData?.metadata?.hasNextPage) {
      setFilters((pre) => ({
        ...pre,
        page: wishlistData?.metadata?.nextPage,
      }));
    }
  };

  return (
    <>
      {wishlistLoading ? (
        <div
          className={classNames(
            "grid md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-3 gap-5 2xl:gap-8",
            listClass
          )}
        >
          {Array.from({ length: 8 }, (_, i) => (
            <EventItemLoader key={i} />
          ))}
        </div>
      ) : wishlistData?.data?.length > 0 ? (
        <div
          className={classNames(
            "grid md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-3 gap-5 2xl:gap-5",
            listClass
          )}
        >
          {wishlistData?.data.map((event) => (
            <EventItem
              key={event?.id || event?._id}
              data={event?.eventData}
              addToFavoriteResponse={addToFavoriteResponse}
            />
          ))}
        </div>
      ) : (
        <NoData image={noDataImage} message="Event not found" />
      )}
      {
        wishlistData?.data?.length > 0 && (
          // wishlistData?.data?.length < wishlistData?.metadata?.totalDocs && (
          <div className="flex space-y-4 items-center justify-between mt-6">
            <div className="text-deep-purple text-xl 2xl:text-2xl font-semibold text-center">
              {t("Showing")} {wishlistData?.data?.length} {t("of")}{" "}
              {wishlistData?.metadata?.totalDocs}
            </div>
            {wishlistData?.metadata?.hasNextPage && (
              <div className="flex justify-center">
                <Button
                  buttonLabel={t("Load More")}
                  buttonLabelClasses="text-lg uppercase text-white"
                  buttonClasses="h-14 bg-secondary-gradient"
                  buttonFunction={loadMore}
                  {...(wishlistFetching && {
                    loading: true,
                    buttonIconPosition: "left",
                    isDisable: true,
                  })}
                />
              </div>
            )}
          </div>
        )
        // )
      }
    </>
  );
};

export default EventsList;
